import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  Save,
  Search,
  Filter,
  Briefcase,
  Shield
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  IndustryCode,
  IndustryClassificationSettings as IndustryClassificationSettingsType,
  createDefaultIndustryClassifications,
} from '@/features/merchant-application/types/industryClassifications';
import type { ApplicationSource } from '@/features/application-sources/types';

interface IndustryClassificationSettingsProps {
  onSettingsChange?: (hasChanges: boolean) => void;
  onSave?: () => void;
}

export const IndustryClassificationSettings: React.FC<IndustryClassificationSettingsProps> = ({
  onSettingsChange,
  onSave,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEnabledOnly, setShowEnabledOnly] = useState(false);
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [availableSources, setAvailableSources] = useState<Array<{ source: string; displayName: string }>>([]);
  const [allIndustrySettings, setAllIndustrySettings] = useState<Record<string, IndustryClassificationSettingsType>>({});
  const [currentSourceSettings, setCurrentSourceSettings] = useState<IndustryClassificationSettingsType>(
    createDefaultIndustryClassifications()
  );

  // Helper to merge saved settings with defaults (to include new codes from codebase)
  const mergeWithDefaults = (saved: IndustryClassificationSettingsType) => {
    const defaults = createDefaultIndustryClassifications();
    const merged: IndustryClassificationSettingsType = {
      traditionalCodes: {},
      payfacCodes: {}
    };

    // Merge Traditional
    Object.keys(defaults.traditionalCodes).forEach(key => {
      if (saved.traditionalCodes && saved.traditionalCodes[key]) {
        merged.traditionalCodes[key] = saved.traditionalCodes[key];
      } else {
        merged.traditionalCodes[key] = defaults.traditionalCodes[key];
      }
    });

    // Merge PayFac
    Object.keys(defaults.payfacCodes).forEach(key => {
      if (saved.payfacCodes && saved.payfacCodes[key]) {
        merged.payfacCodes[key] = saved.payfacCodes[key];
      } else {
        merged.payfacCodes[key] = defaults.payfacCodes[key];
      }
    });

    return merged;
  };

  // Notify parent of changes
  useEffect(() => {
    onSettingsChange?.(hasChanges);
  }, [hasChanges, onSettingsChange]);

  // Load all industry classifications from single document
  const loadIndustryClassifications = useCallback(async () => {
    if (!user?.companyId) return;

    try {
      setLoading(true);

      // Load sources from applicationSources collection
      const sourcesQuery = query(
        collection(db, 'applicationSources'),
        orderBy('order', 'asc')
      );
      const sourcesSnapshot = await getDocs(sourcesQuery);
      const loadedSources = sourcesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as ApplicationSource[];

      // Map to the format needed for the select dropdown
      const sources = loadedSources
        .filter(source => source.isActive)
        .map(source => ({
          source: source.name,
          displayName: source.displayName,
        }));
      setAvailableSources(sources);

      // Load default source from GLOBAL merchant settings
      const settingsDoc = await getDoc(
        doc(db, 'merchantApplicationSettings', 'config')
      );

      let defaultSourceName: string | null = null;
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        defaultSourceName = settings.defaultSource || null;
      }

      // Load industry classifications from GLOBAL collection
      const industryDoc = await getDoc(
        doc(db, 'merchantIndustryClassifications', 'config')
      );

      let industryData: Record<string, IndustryClassificationSettingsType> = {};

      if (industryDoc.exists()) {
        const data = industryDoc.data();
        industryData = data.sources || {};
      }

      setAllIndustrySettings(industryData);

      // Helper to merge saved settings with defaults (to include new codes from codebase)
      const mergeWithDefaults = (saved: IndustryClassificationSettingsType) => {
        const defaults = createDefaultIndustryClassifications();
        const merged: IndustryClassificationSettingsType = {
          traditionalCodes: {},
          payfacCodes: {}
        };

        // Merge Traditional
        Object.keys(defaults.traditionalCodes).forEach(key => {
          if (saved.traditionalCodes && saved.traditionalCodes[key]) {
            merged.traditionalCodes[key] = saved.traditionalCodes[key];
          } else {
            merged.traditionalCodes[key] = defaults.traditionalCodes[key];
          }
        });

        // Merge PayFac
        Object.keys(defaults.payfacCodes).forEach(key => {
          if (saved.payfacCodes && saved.payfacCodes[key]) {
            merged.payfacCodes[key] = saved.payfacCodes[key];
          } else {
            merged.payfacCodes[key] = defaults.payfacCodes[key];
          }
        });

        return merged;
      };

      // Set default selected source and load its settings
      if (sources.length > 0 && !selectedSource) {
        // Use default source if set, otherwise use first source
        const defaultSrc = defaultSourceName || sources[0].source;
        setSelectedSource(defaultSrc);

        // Load settings for default source
        if (industryData[defaultSrc]) {
          setCurrentSourceSettings(mergeWithDefaults(industryData[defaultSrc]));
        } else {
          setCurrentSourceSettings(createDefaultIndustryClassifications());
        }
      }

      setHasChanges(false);
    } catch (error) {
      console.error('Error loading industry classifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load industry classifications',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [user?.companyId, toast, selectedSource]);

  useEffect(() => {
    loadIndustryClassifications();
  }, [loadIndustryClassifications]);

  // When source changes, load that source's industry classifications from local state
  useEffect(() => {
    if (selectedSource && allIndustrySettings) {
      if (allIndustrySettings[selectedSource]) {
        setCurrentSourceSettings(mergeWithDefaults(allIndustrySettings[selectedSource]));
      } else {
        setCurrentSourceSettings(createDefaultIndustryClassifications());
      }
      setHasChanges(false);
    }
  }, [selectedSource, allIndustrySettings]);

  // Save settings to Firestore (single document with sources)
  const saveSettings = async () => {
    if (!user?.companyId || !selectedSource) return;

    try {
      setSaving(true);

      // Update the specific source in the all settings
      const updatedAllSettings = {
        ...allIndustrySettings,
        [selectedSource]: currentSourceSettings,
      };

      // Save to single industryClassifications document with sources structure
      // Save to GLOBAL collection
      await setDoc(
        doc(db, 'merchantIndustryClassifications', 'config'),
        { sources: updatedAllSettings }
      );

      // Update local state
      setAllIndustrySettings(updatedAllSettings);

      toast({
        title: 'Success',
        description: `Industry classifications saved for ${availableSources.find(s => s.source === selectedSource)?.displayName || selectedSource}`,
      });

      setHasChanges(false);
      onSave?.();
    } catch (error) {
      console.error('Error saving industry classifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to save industry classifications',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Toggle industry code enabled status
  const toggleIndustryCode = (type: 'traditional' | 'payfac', key: string) => {
    setHasChanges(true);
    setCurrentSourceSettings((prev) => {
      const targetCodes = type === 'traditional' ? prev.traditionalCodes : prev.payfacCodes;
      const updatedCode = { ...targetCodes[key], enabled: !targetCodes[key].enabled };

      return {
        ...prev,
        [type === 'traditional' ? 'traditionalCodes' : 'payfacCodes']: {
          ...targetCodes,
          [key]: updatedCode,
        },
      };
    });
  };

  // Enable all codes
  const enableAll = (type: 'traditional' | 'payfac') => {
    setHasChanges(true);
    setCurrentSourceSettings((prev) => {
      const targetCodes = type === 'traditional' ? prev.traditionalCodes : prev.payfacCodes;
      const updatedCodes = Object.keys(targetCodes).reduce((acc, key) => {
        acc[key] = { ...targetCodes[key], enabled: true };
        return acc;
      }, {} as Record<string, IndustryCode>);

      return {
        ...prev,
        [type === 'traditional' ? 'traditionalCodes' : 'payfacCodes']: updatedCodes,
      };
    });
  };

  // Disable all codes
  const disableAll = (type: 'traditional' | 'payfac') => {
    setHasChanges(true);
    setCurrentSourceSettings((prev) => {
      const targetCodes = type === 'traditional' ? prev.traditionalCodes : prev.payfacCodes;
      const updatedCodes = Object.keys(targetCodes).reduce((acc, key) => {
        acc[key] = { ...targetCodes[key], enabled: false };
        return acc;
      }, {} as Record<string, IndustryCode>);

      return {
        ...prev,
        [type === 'traditional' ? 'traditionalCodes' : 'payfacCodes']: updatedCodes,
      };
    });
  };

  // Filter codes based on search term
  const filterCodes = (codes: Record<string, IndustryCode>) => {
    const filtered = Object.entries(codes).filter(([_, code]) => {
      const matchesSearch =
        !searchTerm ||
        code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.identifier?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter = !showEnabledOnly || code.enabled;

      return matchesSearch && matchesFilter;
    });

    return filtered.sort((a, b) => a[1].code.localeCompare(b[1].code));
  };

  // Get counts
  const getEnabledCount = (codes: Record<string, IndustryCode>) => {
    return Object.values(codes).filter((code) => code.enabled).length;
  };

  const getTotalCount = (codes: Record<string, IndustryCode>) => {
    return Object.keys(codes).length;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  const traditionalFiltered = filterCodes(currentSourceSettings.traditionalCodes);
  const payfacFiltered = filterCodes(currentSourceSettings.payfacCodes);
  const traditionalEnabledCount = getEnabledCount(currentSourceSettings.traditionalCodes);
  const traditionalTotalCount = getTotalCount(currentSourceSettings.traditionalCodes);
  const payfacEnabledCount = getEnabledCount(currentSourceSettings.payfacCodes);
  const payfacTotalCount = getTotalCount(currentSourceSettings.payfacCodes);

  return (
    <div className="space-y-6">
      {/* Control Panel */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50/50 rounded-xl p-6 border border-green-200/30">
        <div className="flex flex-col gap-4">
          {/* Top Row - Source Selector */}
          <div className="flex items-center pb-3 border-b border-green-200/30">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Application Source:</label>
              <Select
                value={selectedSource}
                onValueChange={(value: string) => setSelectedSource(value)}
              >
                <SelectTrigger className="w-[280px] bg-white">
                  <SelectValue placeholder="Select a source..." />
                </SelectTrigger>
                <SelectContent>
                  {availableSources.map((source) => (
                    <SelectItem key={source.source} value={source.source}>
                      {source.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Second Row - Actions and Stats */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="bg-red-100 text-red-700 border-0 px-3 py-1">
                  <Shield className="h-3 w-3 mr-1" />
                  Traditional: {traditionalEnabledCount}/{traditionalTotalCount}
                </Badge>
                <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-0 px-3 py-1">
                  <Briefcase className="h-3 w-3 mr-1" />
                  PayFac: {payfacEnabledCount}/{payfacTotalCount}
                </Badge>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={saveSettings}
                disabled={saving || !hasChanges}
                size="sm"
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg shadow-green-600/20"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Bottom Row - Search and Filter */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by code or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEnabledOnly(!showEnabledOnly)}
              className={cn(
                'transition-colors h-10',
                showEnabledOnly && 'bg-green-50 border-green-200 text-green-700'
              )}
            >
              <Filter className="h-4 w-4 mr-2" />
              {showEnabledOnly ? 'Enabled Only' : 'Show All'}
            </Button>
          </div>
        </div>
      </div>

      {/* Two-Column Layout: Traditional | PayFac */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Traditional Codes Column */}
        <div className="bg-white rounded-xl border border-red-200/50 overflow-hidden">
          <div className="p-4 bg-red-50/50 border-b border-red-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-red-600" />
              <div>
                <h3 className="font-semibold text-gray-900">Traditional (High-Risk)</h3>
                <p className="text-xs text-gray-600">
                  {traditionalEnabledCount}/{traditionalTotalCount} enabled
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => enableAll('traditional')}
                className="h-7 px-2 text-xs"
              >
                Enable All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => disableAll('traditional')}
                className="h-7 px-2 text-xs"
              >
                Disable All
              </Button>
            </div>
          </div>

          <div className="p-4 max-h-[600px] overflow-y-auto">
            {traditionalFiltered.length === 0 ? (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-sm text-gray-500">No codes found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {traditionalFiltered.map(([key, code]) => (
                  <div
                    key={key}
                    className={cn(
                      'flex items-center gap-3 p-2.5 rounded-lg border transition-all duration-150 hover:shadow-sm cursor-pointer',
                      code.enabled
                        ? 'border-red-200 bg-red-50/40'
                        : 'border-gray-200 bg-gray-50/30 hover:bg-gray-50'
                    )}
                    onClick={() => toggleIndustryCode('traditional', key)}
                  >
                    <Checkbox
                      checked={code.enabled}
                      onCheckedChange={() => toggleIndustryCode('traditional', key)}
                      className="shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge
                          variant="secondary"
                          className={cn(
                            'text-xs font-mono',
                            code.enabled
                              ? 'bg-red-100 text-red-700 border-0'
                              : 'bg-gray-100 text-gray-600 border-0'
                          )}
                        >
                          {code.code}
                        </Badge>
                      </div>
                      <p className={cn(
                        'text-sm',
                        code.enabled ? 'text-gray-900 font-medium' : 'text-gray-600'
                      )}>
                        {code.description}
                      </p>
                      {code.identifier && (
                        <p className="text-xs text-gray-500 mt-0.5 font-mono">
                          {code.identifier}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* PayFac Codes Column */}
        <div className="bg-white rounded-xl border border-blue-200/50 overflow-hidden">
          <div className="p-4 bg-blue-50/50 border-b border-blue-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Briefcase className="h-5 w-5 text-blue-600" />
              <div>
                <h3 className="font-semibold text-gray-900">PayFac (Standard)</h3>
                <p className="text-xs text-gray-600">
                  {payfacEnabledCount}/{payfacTotalCount} enabled
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => enableAll('payfac')}
                className="h-7 px-2 text-xs"
              >
                Enable All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => disableAll('payfac')}
                className="h-7 px-2 text-xs"
              >
                Disable All
              </Button>
            </div>
          </div>

          <div className="p-4 max-h-[600px] overflow-y-auto">
            {payfacFiltered.length === 0 ? (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-sm text-gray-500">No codes found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {payfacFiltered.map(([key, code]) => (
                  <div
                    key={key}
                    className={cn(
                      'flex items-center gap-3 p-2.5 rounded-lg border transition-all duration-150 hover:shadow-sm cursor-pointer',
                      code.enabled
                        ? 'border-blue-200 bg-blue-50/40'
                        : 'border-gray-200 bg-gray-50/30 hover:bg-gray-50'
                    )}
                    onClick={() => toggleIndustryCode('payfac', key)}
                  >
                    <Checkbox
                      checked={code.enabled}
                      onCheckedChange={() => toggleIndustryCode('payfac', key)}
                      className="shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge
                          variant="secondary"
                          className={cn(
                            'text-xs font-mono',
                            code.enabled
                              ? 'bg-blue-100 text-blue-700 border-0'
                              : 'bg-gray-100 text-gray-600 border-0'
                          )}
                        >
                          {code.code}
                        </Badge>
                      </div>
                      <p className={cn(
                        'text-sm',
                        code.enabled ? 'text-gray-900 font-medium' : 'text-gray-600'
                      )}>
                        {code.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
